import React, { useState, useEffect, useCallback, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { CCTVMonitor } from "../components/cctv/cctv-monitor";
import { getAuthToken, getTenantId, getCurrentUser } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  Camera, 
  Video, 
  Shield, 
  AlertTriangle,
  CheckCircle,
  Clock,
  Monitor,
  Eye,
  ScanLine,
  RefreshCw
} from "lucide-react";

const accessTenant = {
    "id": "5068b5ba-34a7-4088-a6c7-f1cb26d6667c",
    "tenantCode": "TN90009676",
    "name": "Access World ",
    "domain": null,
    "contactEmail": "access@weigh8.co.za",
    "contactPhone": "",
    "address": null,
    "isActive": true,
    "settings": {},
    "createdAt": "2025-10-24T07:13:29.711Z",
    "updatedAt": "2025-10-24T07:13:29.711Z"
}

const bulkConnectionsTenant = {
    "id": "aaeab356-025b-4dcf-a380-03c181b5f7c1",
    "tenantCode": "TN12649205",
    "name": "Bulk Connections",
    "domain": null,
    "contactEmail": "bulkConnections@weigh8.co.za",
    "contactPhone": null,
    "address": null,
    "isActive": true,
    "settings": {},
    "createdAt": "2025-10-21T02:10:50.028Z",
    "updatedAt": "2025-10-21T02:10:50.028Z"
}

const LIONS_PARK_TENANT_CODE = "TN12461839";
const BULK_CONNECTIONS_TENANT_CODE = "TN12649205";

export default function CCTV() {
  const [selectedCamera, setSelectedCamera] = useState<string | undefined>(undefined);
  const [activeTab, setActiveTab] = useState<'general' | 'anpr' | 'facial' | 'weighbridge1' | 'weighbridge2' | 'weighbridge3'>('general');
  const [viewMode, setViewMode] = useState<'single' | 'grid'>('single');
  const [cameraLoadStates, setCameraLoadStates] = useState<Record<string, 'loading' | 'loaded' | 'error'>>({});
  const [cameraImages, setCameraImages] = useState<Record<string, { image: string; timestamp: string; filename: string }>>({});
  const [isLoadingImages, setIsLoadingImages] = useState(true);
  const [allFiles, setAllFiles] = useState<any[]>([]);
  const fetchingRef = useRef<Set<string>>(new Set());
  
  const currentUser =   getCurrentUser();
   const currentTenantId  = getTenantId();
     const { data: currentTenant } = useQuery({
    queryKey: ["/api/tenants/current"],
    queryFn: async () => {
      const token = getAuthToken();
      const tenantId = getTenantId();
      
      const response = await fetch("/api/tenants/current", {
        headers: { 
          "Authorization": `Bearer ${token}`,
          "X-Tenant-Id": tenantId || ""
        }
      });
      if (!response.ok) return null;
      return response.json();
    },
  });
  
  const isLionsParkTenant = currentTenant?.tenantCode === LIONS_PARK_TENANT_CODE;
  const isBulkConnectionsTenant = currentTenant?.tenantCode === BULK_CONNECTIONS_TENANT_CODE;
  // log current tenant info
  console.log("Current Tenant:", currentTenant);
  const isPortUser = currentUser?.role === 'port';
  const { data: combinedCameras = [], isLoading } = useQuery({
    queryKey: ["lions-park-cameras"],
    enabled: isLionsParkTenant, //  fetch if Lions Park tenant
    refetchInterval: isLionsParkTenant ? 30000 : false, 
    queryFn: async () => {
      console.log("🔄 Fetching Lions Park cameras via proxy from /api/lions-park/cameras");
      const token = getAuthToken();
      const tenantId = getTenantId();
      
      const response = await fetch("/api/lions-park/cameras", {
        headers: { 
          "Authorization": `Bearer ${token}`,
          "X-Tenant-Id": tenantId || ""
        }
      });
      if (!response.ok) throw new Error(`Failed to fetch Lions Park cameras: ${response.status}`);
      
      const camerasData = await response.json();
      console.log(`✅ Received ${camerasData.length} cameras from Lions Park proxy`);
      
      
      return camerasData;
    },
  });

  const { data: anprPlates = [], isLoading: anprLoading } = useQuery({
    queryKey: ["anpr-plates"],
    refetchInterval: 10000, //  every 10 secondds
    queryFn: async () => {
      console.log("🚗 Fetching ANPR plates via proxy from /api/anpr/plates/latest");
      const token = getAuthToken();
      const tenantId = getTenantId();
      
      const response = await fetch("/api/anpr/plates/latest", {
        headers: { 
          "Authorization": `Bearer ${token}`,
          "X-Tenant-Id": tenantId || ""
        }
      });
      
      if (!response.ok) {
        console.error(`Failed to fetch ANPR plates: ${response.status}`);
        return [];
      }
      
      const plates = await response.json();
      console.log(`✅ Received ${plates.length} ANPR plate detections with images`);
      
      return plates;
    },
  });

  const weighbridgeCameras = {
    weighbridge1: { // Internal-Weighbridge
      name: "Weighbridge 1 (Internal)",
      cameras: [
        { id: "iwb1-back", name: "Back Cam", ip: "192.168.10.17", streamUrl: "http://192.168.10.17/doc/page/preview.asp" },
        { id: "iwb1-top1", name: "Top Cam1", ip: "192.168.10.18", streamUrl: "http://192.168.10.18/doc/page/preview.asp" },
        { id: "iwb1-top2", name: "Top Cam2", ip: "192.168.10.19", streamUrl: "http://192.168.10.19/doc/page/preview.asp" },
        { id: "iwb1-lpr", name: "LPR Cam", ip: "192.168.10.20", streamUrl: "http://192.168.10.20/doc/page/preview.asp" },
        { id: "iwb1-front", name: "Front Cam", ip: "192.168.10.21", streamUrl: "http://192.168.10.21/doc/page/preview.asp" }
      ]
    },
    weighbridge2: { // Out-Weighbridge
      name: "Weighbridge 2 (Out)",
      cameras: [
        { id: "owb2-back", name: "Back Cam", ip: "192.168.10.22", streamUrl: "http://192.168.10.22/doc/page/preview.asp" },
        { id: "owb2-top1", name: "Top Cam1", ip: "192.168.10.23", streamUrl: "http://192.168.10.23/doc/page/preview.asp" },
        { id: "owb2-top2", name: "Top Cam2", ip: "192.168.10.24", streamUrl: "http://192.168.10.24/doc/page/preview.asp" },
        { id: "owb2-lpr", name: "LPR Cam", ip: "192.168.10.25", streamUrl: "http://192.168.10.25/doc/page/preview.asp" },
        { id: "owb2-front", name: "Front Cam", ip: "192.168.10.26", streamUrl: "http://192.168.10.26/doc/page/preview.asp" }
      ]
    },
    weighbridge3: { // In-Weighbridge
      name: "Weighbridge 3 (In)",
      cameras: [
        { id: "iwb3-back", name: "Back Cam", ip: "192.168.10.27", streamUrl: "http://192.168.10.27/doc/page/preview.asp" },
        { id: "iwb3-top1", name: "Top Cam1", ip: "192.168.10.28", streamUrl: "http://192.168.10.28/doc/page/preview.asp" },
        { id: "iwb3-top2", name: "Top Cam2", ip: "192.168.10.12", streamUrl: "http://192.168.10.12/doc/page/preview.asp" },
        { id: "iwb3-lpr", name: "LPR Cam", ip: "192.168.10.29", streamUrl: "http://192.168.10.29/doc/page/preview.asp" },
        { id: "iwb3-front", name: "Front Cam", ip: "192.168.10.30", streamUrl: "http://192.168.10.30/doc/page/preview.asp" }
      ]
    }
  };

  const securityStats = {
    totalCameras: combinedCameras.length + anprPlates.length,
    onlineCameras: combinedCameras.filter((cam: any) => cam.isActive !== false).length + anprPlates.length,
    recordingCameras: combinedCameras.length, // All Lions Park cameras are actively recording
    alerts: combinedCameras.filter((cam: any) => cam.isActive === false).length,
    storageUsed: 78.5,
    lastIncident: anprPlates.length > 0 ? 
      `Latest ANPR: ${anprPlates[0].plateNumber} at ${anprPlates[0].location}` : 
      (combinedCameras.length > 0 ? `Lions Park - ${combinedCameras.length} cameras active` : "No recent activity"),
    byCategory: {
      general: { 
        total: combinedCameras.filter((cam: any) => cam.category === 'general').length,
        online: combinedCameras.filter((cam: any) => cam.category === 'general' && cam.isActive !== false).length
      },
      anpr: { 
        total: anprPlates.length || combinedCameras.filter((cam: any) => cam.category === 'anpr').length,
        online: anprPlates.length || combinedCameras.filter((cam: any) => cam.category === 'anpr' && cam.isActive !== false).length
      },
      facial: { 
        total: combinedCameras.filter((cam: any) => cam.category === 'facial').length,
        online: combinedCameras.filter((cam: any) => cam.category === 'facial' && cam.isActive !== false).length
      }
    }
  };

  // Create recent events from camera updates
  const recentEvents = combinedCameras.slice(0, 10).map((camera: any, index: number) => ({
    id: camera.id || index + 1,
    type: "camera_update",
    camera: camera.name,
    time: new Date(camera.lastUpdate).toLocaleTimeString('en-US', { 
      hour12: false, 
      hour: '2-digit', 
      minute: '2-digit' 
    }),
    description: `Camera ${camera.cameraId} updated - ${camera.isActive ? 'Active' : 'Inactive'}`,
    priority: camera.isActive ? "medium" : "high"
  }));

  // Set default selected camera to first Lions Park camera
  React.useEffect(() => {
    if (combinedCameras.length > 0 && !selectedCamera) {
      setSelectedCamera(combinedCameras[0].id);
    }
  }, [combinedCameras, selectedCamera]);

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200';
      case 'medium': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getEventIcon = (type: string) => {
    switch (type) {
      case 'motion': return <Shield className="w-4 h-4" />;
      case 'alert': return <AlertTriangle className="w-4 h-4" />;
      case 'recording': return <Video className="w-4 h-4" />;
      default: return <Camera className="w-4 h-4" />;
    }
  };

  const handleCameraLoad = (cameraId: string) => {
    setCameraLoadStates(prev => ({ ...prev, [cameraId]: 'loaded' }));
  };

  const handleCameraError = (cameraId: string) => {
    setCameraLoadStates(prev => ({ ...prev, [cameraId]: 'error' }));
  };

  const fetchAllFiles = useCallback(async () => {
    // if (!isBulkConnectionsTenant) {
    //   return []
    // }
    try {
      console.log('📁 Fetching all files from FTP...');
      const filesResponse = await fetch('/api/moxa/files');
      const filesData = await filesResponse.json();
      
      if (!filesData.success) {
        throw new Error('Failed to fetch files');
      }
      
      console.log(` Fetched ${filesData.files.length} image files`);
      setAllFiles(filesData.files);
      
      await loadCameraImages(filesData.files);
      
    } catch (error) {
      console.error('Failed to fetch files:', error);
    }
  }, []);

  const loadCameraImages = useCallback(async (files: any[]) => {
    const cameraPatterns: Record<string, string[]> = {
      'iwb1-back': ['internal-weighbridge-back-cam'],
      'iwb1-top1': ['internal-weighbridge-top1'],
      'iwb1-top2': ['internal-weighbridge-top2'],
      'iwb1-lpr': ['internal-weighbridge-lpr'],
      'iwb1-front': ['interal-weighbridge-front'],
      
      'owb2-back': ['out-weighbridge-back'],
      'owb2-top1': ['out-weighbridge-top-cam1'],
      'owb2-top2': ['out-weighbridge-top-cam2'],
      'owb2-lpr': ['out-weighbridge-lpr'],
      'owb2-front': ['out-weighbridge-front cam'],
      
      'iwb3-back': ['in-weighbridge-back-cam'],
      'iwb3-top1': ['in-weighbridge-topcam1'],
      'iwb3-top2': ['in-weighbridge-top-cam2'],
      'iwb3-lpr': ['in-weighbridge-lpr'],
      'iwb3-front': ['in-weighbridge-front']
    };

    for (const [cameraId, patterns] of Object.entries(cameraPatterns)) {
      try {
        console.log(` Looking for ${cameraId} with patterns: ${patterns.join(', ')}`);
        setCameraLoadStates(prev => ({ ...prev, [cameraId]: 'loading' }));
        
        let foundFile = null;
        for (const file of files) {
          const fileName = file.name.toLowerCase();
          const matches = patterns.some(pattern => fileName.includes(pattern));
          
          if (matches) {
            foundFile = file;
            console.log(` Found match for ${cameraId}: ${file.name} (matched pattern: ${patterns.find(p => fileName.includes(p))})`);
            break; 
          }
        }
        
        if (foundFile) {
          console.log(`📷 Loading image for ${cameraId}: ${foundFile.name}`);
          
          //   actual image
          const imageResponse = await fetch(`/api/moxa/files/${foundFile.name}`);
          const imageData = await imageResponse.json();
          
          if (imageData.success && imageData.data) {
            const timestamp = foundFile.modifiedAt || new Date().toISOString();
            setCameraImages(prev => ({ 
              ...prev, 
              [cameraId]: { 
                image: imageData.data, 
                timestamp: timestamp,
                filename: foundFile.name
              } 
            }));
            setCameraLoadStates(prev => ({ ...prev, [cameraId]: 'loaded' }));
            console.log(` Loaded image for ${cameraId} at ${timestamp}`);
          } else {
            throw new Error('Failed to load image data');
          }
        } else {
          console.log(` No image found for ${cameraId}`);
          setCameraLoadStates(prev => ({ ...prev, [cameraId]: 'error' }));
        }
        
      } catch (error) {
        console.error(`Failed to load image for ${cameraId}:`, error);
        setCameraLoadStates(prev => ({ ...prev, [cameraId]: 'error' }));
      }
    }
    
    setIsLoadingImages(false);
  }, []);

  const refreshCamera = (cameraId: string) => {
    if (allFiles.length > 0) {
      loadCameraImages(allFiles);
    } else {
      fetchAllFiles();
    }
  };

  useEffect(() => {
    const initialStates: Record<string, 'loading'> = {};
    Object.values(weighbridgeCameras).forEach(weighbridge => {
      weighbridge.cameras.forEach(camera => {
        initialStates[camera.id] = 'loading';
      });
    });
    setCameraLoadStates(initialStates);
    
    fetchAllFiles();
    
    const refreshInterval = setInterval(() => {
      console.log('🔄 Refreshing camera images (1-minute interval)');
      fetchAllFiles();
    }, 60000); 
    
    return () => {
      clearInterval(refreshInterval);
    };
  }, [fetchAllFiles]);

  if (!isLionsParkTenant && !isPortUser) {
    return (
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">CCTV Security System</h1>
            <p className="text-muted-foreground">
              Live monitoring and security surveillance for weighbridge operations
            </p>
          </div>
        </div>
        
        <Card className="border-dashed">
          <CardContent className="pt-6">
            <div className="text-center py-12">
              <Camera className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-30" />
              <h3 className="text-lg font-semibold mb-2">CCTV Access Not Available</h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                CCTV camera monitoring is only available for authorized facilities. Your organization does not have access to this feature.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">CCTV Security System</h1>
              <p className="text-muted-foreground">
                Live monitoring and security surveillance for weighbridge operations
              </p>
            </div>
            
            <div className="flex items-center space-x-3">
              {isPortUser ? (
                <Badge variant="default" className="bg-blue-100 text-blue-800">
                  🏭 Port Operations - Weighbridge Monitoring
                </Badge>
              ) : combinedCameras.length > 0 && (
                <Badge variant="default" className="bg-green-100 text-green-800">
                  🦁 Lions Park Live - {combinedCameras.length} cameras
                </Badge>
              )}
              {isLoading && (
                <Badge variant="outline">
                  Loading Lions Park...
                </Badge>
              )}
              {!isLoading && combinedCameras.length === 0 && (
                <Badge variant="secondary">
                  Lions Park Offline
                </Badge>
              )}
            </div>
          </div>

          {/* Security Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-blue-600">Total Cameras</p>
                    <p className="text-3xl font-bold text-blue-900">{securityStats.totalCameras}</p>
                  </div>
                  <Monitor className="w-8 h-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-green-50 to-white border-green-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-green-600">Online Cameras</p>
                    <p className="text-3xl font-bold text-green-900">{securityStats.onlineCameras}</p>
                  </div>
                  <CheckCircle className="w-8 h-8 text-green-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-red-50 to-white border-red-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-red-600">Active Recordings</p>
                    <p className="text-3xl font-bold text-red-900">{securityStats.recordingCameras}</p>
                  </div>
                  <Video className="w-8 h-8 text-red-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="bg-gradient-to-br from-orange-50 to-white border-orange-200">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-orange-600">Storage Used</p>
                    <p className="text-3xl font-bold text-orange-900">{securityStats.storageUsed}%</p>
                  </div>
                  <AlertTriangle className="w-8 h-8 text-orange-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6">
            {/* Camera Type Tabs */}
            <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as any)} className="w-full">
              <div className="flex items-center justify-between mb-6">
                {isPortUser ? (
                  // Port users see 6 tabs: 3 empty + 3 weighbridge tabs
                  <TabsList className="grid w-full max-w-4xl grid-cols-6">
                    <TabsTrigger value="general" className="flex items-center space-x-2">
                      <Camera className="w-4 h-4" />
                      <span>General (Empty)</span>
                    </TabsTrigger>
                    <TabsTrigger value="anpr" className="flex items-center space-x-2">
                      <ScanLine className="w-4 h-4" />
                      <span>ANPR (Empty)</span>
                    </TabsTrigger>
                    <TabsTrigger value="facial" className="flex items-center space-x-2">
                      <Eye className="w-4 h-4" />
                      <span>Facial (Empty)</span>
                    </TabsTrigger>
                    {isBulkConnectionsTenant && <TabsTrigger value="weighbridge1" className="flex items-center space-x-2">
                      <Monitor className="w-4 h-4" />
                      <span>Weighbridge 1</span>
                    </TabsTrigger>}
                    {isBulkConnectionsTenant && <TabsTrigger value="weighbridge2" className="flex items-center space-x-2">
                      <Monitor className="w-4 h-4" />
                      <span>Weighbridge 2</span>
                    </TabsTrigger>}
                    {isBulkConnectionsTenant && <TabsTrigger value="weighbridge3" className="flex items-center space-x-2">
                      <Monitor className="w-4 h-4" />
                      <span>Weighbridge 3</span>
                    </TabsTrigger>}
                  </TabsList>
                ) : (
                  // Staging users see 3 tabs with camera feeds
                  <TabsList className="grid w-full max-w-md grid-cols-3">
                    <TabsTrigger value="general" className="flex items-center space-x-2">
                      <Camera className="w-4 h-4" />
                      <span>General ({securityStats.byCategory.general.online}/{securityStats.byCategory.general.total})</span>
                    </TabsTrigger>
                    <TabsTrigger value="anpr" className="flex items-center space-x-2">
                      <ScanLine className="w-4 h-4" />
                      <span>ANPR ({securityStats.byCategory.anpr.online}/{securityStats.byCategory.anpr.total})</span>
                    </TabsTrigger>
                    <TabsTrigger value="facial" className="flex items-center space-x-2">
                      <Eye className="w-4 h-4" />
                      <span>Facial ({securityStats.byCategory.facial.online}/{securityStats.byCategory.facial.total})</span>
                    </TabsTrigger>
                  </TabsList>
                )}
              </div>

              <TabsContent value="general" className="mt-0">
                {isPortUser ? (
                  <div className="flex items-center justify-center h-96 bg-gray-100 rounded-lg">
                    <div className="text-center">
                      <Camera className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-gray-600 mb-2">General Cameras</h3>
                      <p className="text-gray-500">This tab is not available for port users</p>
                    </div>
                  </div>
                ) : isLoading ? (
                  <div className="flex items-center justify-center h-96 bg-gray-100 rounded-lg">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                      <p className="text-gray-600">Loading camera feeds...</p>
                    </div>
                  </div>
                ) : (
                  <CCTVMonitor 
                    selectedCamera={selectedCamera}
                    onCameraSelect={setSelectedCamera}
                    viewMode={viewMode}
                    onViewModeChange={setViewMode}
                    cameraFilter="general"
                    cameras={combinedCameras}
                  />
                )}
              </TabsContent>

              <TabsContent value="anpr" className="mt-0">
                {isPortUser ? (
                  <div className="flex items-center justify-center h-96 bg-gray-100 rounded-lg">
                    <div className="text-center">
                      <ScanLine className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-gray-600 mb-2">ANPR Cameras</h3>
                      <p className="text-gray-500">This tab is not available for port users</p>
                    </div>
                  </div>
                ) : anprLoading ? (
                  <div className="flex items-center justify-center h-96 bg-gray-100 rounded-lg">
                    <div className="text-center">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                      <p className="text-gray-600">Loading ANPR detections...</p>
                    </div>
                  </div>
                ) : anprPlates.length > 0 ? (
                  <CCTVMonitor 
                    selectedCamera={selectedCamera}
                    onCameraSelect={setSelectedCamera}
                    viewMode={viewMode}
                    onViewModeChange={setViewMode}
                    cameraFilter="anpr"
                    cameras={anprPlates}
                  />
                ) : (
                  <Card>
                    <CardContent className="p-12 text-center">
                      <ScanLine className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                      <h3 className="text-lg font-semibold mb-2">No ANPR Detections</h3>
                      <p className="text-muted-foreground">
                        No recent plate detections available. ANPR cameras will update automatically.
                      </p>
                    </CardContent>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="facial" className="mt-0">
                {isPortUser ? (
                  <div className="flex items-center justify-center h-96 bg-gray-100 rounded-lg">
                    <div className="text-center">
                      <Eye className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-gray-600 mb-2">Facial Recognition</h3>
                      <p className="text-gray-500">This tab is not available for port users</p>
                    </div>
                  </div>
                ) : (
                  <CCTVMonitor 
                    selectedCamera={selectedCamera}
                    onCameraSelect={setSelectedCamera}
                    viewMode={viewMode}
                    onViewModeChange={setViewMode}
                    cameraFilter="facial"
                    cameras={combinedCameras}
                  />
                )}
              </TabsContent>

              {/* Weighbridge tabs for port users */}
              <TabsContent value="weighbridge1" className="mt-0">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold">{weighbridgeCameras.weighbridge1.name}</h3>
                    <Badge variant="outline" className="text-green-600 border-green-600">
                      {weighbridgeCameras.weighbridge1.cameras.length} Cameras
                    </Badge>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {weighbridgeCameras.weighbridge1.cameras.map((camera) => (
                      <Card key={camera.id} className="overflow-hidden">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm font-medium">{camera.name}</CardTitle>
                          <p className="text-xs text-muted-foreground">IP: {camera.ip}</p>
                        </CardHeader>
                        <CardContent className="p-0">
                          <div className="aspect-video bg-gray-100 relative overflow-hidden">
                            {cameraLoadStates[camera.id] === 'loaded' && cameraImages[camera.id] && (
                              <>
                                <img
                                  src={cameraImages[camera.id].image}
                                  alt={`${camera.name} Feed`}
                                  className="w-full h-full object-cover"
                                  onError={() => handleCameraError(camera.id)}
                                />
                                <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                                  {new Date(cameraImages[camera.id].timestamp).toLocaleString()}
                                </div>
                              </>
                            )}
                            {cameraLoadStates[camera.id] === 'loading' && (
                              <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                                <div className="text-center">
                                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                                  <p className="text-xs text-gray-500">Loading Image...</p>
                                </div>
                              </div>
                            )}
                            {cameraLoadStates[camera.id] === 'error' && (
                              <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                                <div className="text-center">
                                  <Monitor className="w-8 h-8 text-red-400 mx-auto mb-2" />
                                  <p className="text-xs text-red-500">Image Unavailable</p>
                                  <p className="text-xs text-gray-400 mb-2">{camera.name}</p>
                                  <button
                                    onClick={() => refreshCamera(camera.id)}
                                    className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 mx-auto"
                                  >
                                    <RefreshCw className="w-3 h-3" />
                                    Retry
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="weighbridge2" className="mt-0">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold">{weighbridgeCameras.weighbridge2.name}</h3>
                    <Badge variant="outline" className="text-green-600 border-green-600">
                      {weighbridgeCameras.weighbridge2.cameras.length} Cameras
                    </Badge>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {weighbridgeCameras.weighbridge2.cameras.map((camera) => (
                      <Card key={camera.id} className="overflow-hidden">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm font-medium">{camera.name}</CardTitle>
                          <p className="text-xs text-muted-foreground">IP: {camera.ip}</p>
                        </CardHeader>
                        <CardContent className="p-0">
                          <div className="aspect-video bg-gray-100 relative overflow-hidden">
                            {cameraLoadStates[camera.id] === 'loaded' && cameraImages[camera.id] && (
                              <>
                                <img
                                  src={cameraImages[camera.id].image}
                                  alt={`${camera.name} Feed`}
                                  className="w-full h-full object-cover"
                                  onError={() => handleCameraError(camera.id)}
                                />
                                <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                                  {new Date(cameraImages[camera.id].timestamp).toLocaleString()}
                                </div>
                              </>
                            )}
                            {cameraLoadStates[camera.id] === 'loading' && (
                              <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                                <div className="text-center">
                                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                                  <p className="text-xs text-gray-500">Loading Image...</p>
                                </div>
                              </div>
                            )}
                            {cameraLoadStates[camera.id] === 'error' && (
                              <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                                <div className="text-center">
                                  <Monitor className="w-8 h-8 text-red-400 mx-auto mb-2" />
                                  <p className="text-xs text-red-500">Image Unavailable</p>
                                  <p className="text-xs text-gray-400 mb-2">{camera.name}</p>
                                  <button
                                    onClick={() => refreshCamera(camera.id)}
                                    className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 mx-auto"
                                  >
                                    <RefreshCw className="w-3 h-3" />
                                    Retry
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="weighbridge3" className="mt-0">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold">{weighbridgeCameras.weighbridge3.name}</h3>
                    <Badge variant="outline" className="text-green-600 border-green-600">
                      {weighbridgeCameras.weighbridge3.cameras.length} Cameras
                    </Badge>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {weighbridgeCameras.weighbridge3.cameras.map((camera) => (
                      <Card key={camera.id} className="overflow-hidden">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm font-medium">{camera.name}</CardTitle>
                          <p className="text-xs text-muted-foreground">IP: {camera.ip}</p>
                        </CardHeader>
                        <CardContent className="p-0">
                          <div className="aspect-video bg-gray-100 relative overflow-hidden">
                            {cameraLoadStates[camera.id] === 'loaded' && cameraImages[camera.id] && (
                              <>
                                <img
                                  src={cameraImages[camera.id].image}
                                  alt={`${camera.name} Feed`}
                                  className="w-full h-full object-cover"
                                  onError={() => handleCameraError(camera.id)}
                                />
                                <div className="absolute bottom-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                                  {new Date(cameraImages[camera.id].timestamp).toLocaleString()}
                                </div>
                              </>
                            )}
                            {cameraLoadStates[camera.id] === 'loading' && (
                              <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                                <div className="text-center">
                                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                                  <p className="text-xs text-gray-500">Loading Image...</p>
                                </div>
                              </div>
                            )}
                            {cameraLoadStates[camera.id] === 'error' && (
                              <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                                <div className="text-center">
                                  <Monitor className="w-8 h-8 text-red-400 mx-auto mb-2" />
                                  <p className="text-xs text-red-500">Image Unavailable</p>
                                  <p className="text-xs text-gray-400 mb-2">{camera.name}</p>
                                  <button
                                    onClick={() => refreshCamera(camera.id)}
                                    className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600 mx-auto"
                                  >
                                    <RefreshCw className="w-3 h-3" />
                                    Retry
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>

    </div>
  );
}

// Camera Category Information Component
function CameraCategoryInfo({ category }: { category: 'general' | 'anpr' | 'facial' }) {
  const recentEvents = [
    {
      id: 1,
      type: "motion",
      camera: "Gate Entrance",
      time: "14:32",
      description: "Vehicle detected at main entrance",
      priority: "low"
    },
    {
      id: 2,
      type: "alert",
      camera: "Exit Gate ANPR", 
      time: "14:15",
      description: "Camera offline - Connection lost",
      priority: "high"
    },
    {
      id: 3,
      type: "recording",
      camera: "Driver Face Recognition",
      time: "14:08",
      description: "Recording started - Face ID verification",
      priority: "medium"
    }
  ];

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800 border-red-200';
      case 'medium': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getEventIcon = (type: string) => {
    switch (type) {
      case 'motion': return <Shield className="w-4 h-4" />;
      case 'alert': return <AlertTriangle className="w-4 h-4" />;
      case 'recording': return <Video className="w-4 h-4" />;
      default: return <Camera className="w-4 h-4" />;
    }
  };

  const categoryInfo = {
    general: {
      title: "General Surveillance",
      description: "Standard security cameras for overall site monitoring",
      features: ["Wide area coverage", "24/7 recording", "Motion detection", "Night vision"],
      locations: ["Gate Entrance", "Scale Platforms", "Parking Areas"]
    },
    anpr: {
      title: "License Plate Recognition",
      description: "Automated Number Plate Recognition for vehicle identification",
      features: ["Real-time plate reading", "Database lookup", "Alert generation", "High-res capture"],
      locations: ["Entry Gates", "Exit Points", "Scale Approaches"]
    },
    facial: {
      title: "Facial Recognition",
      description: "Biometric identification for enhanced security and access control",
      features: ["Face detection", "Identity verification", "Access logging", "Anti-spoofing"],
      locations: ["Security Checkpoints", "Driver Stations", "Admin Entrances"]
    }
  };

  const info = categoryInfo[category];
  const getIcon = () => {
    switch (category) {
      case 'general': return <Camera className="w-5 h-5" />;
      case 'anpr': return <ScanLine className="w-5 h-5" />;
      case 'facial': return <Eye className="w-5 h-5" />;
    }
  };

  return null;
}